import React, { FC, useCallback, useContext } from 'react'
import { PaginationWrapper, PageButton, PaginationIcon, PaginationDots } from './styles'
import ChevronRight from '../../../assets/icons/chevron-right.svg'
import ChevronLeft from '../../../assets/icons/chevron-left.svg'
import type { StyledSvgComponent } from '../../../types/svg'
import { ScreenContext } from 'react-components'

interface PaginationProps {
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
}

const Pagination: FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
    const { isDesktop, isTablet } = useContext(ScreenContext)

    const handlePrevious = useCallback(() => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1)
        }
    }, [currentPage, onPageChange])

    const handleNext = useCallback(() => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1)
        }
    }, [currentPage, totalPages, onPageChange])

    const handlePageClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const page = Number(event.currentTarget.dataset.page)
            onPageChange(page)
        },
        [onPageChange],
    )

    if (totalPages <= 1) {
        return null
    }

    const getVisiblePages = () => {
        const pages_around_current = isDesktop || isTablet ? 2 : 1
        const range = []
        const rangeWithDots: (number | string)[] = []
        let previousPage: number | undefined

        range.push(1)
        for (let i = currentPage - pages_around_current; i <= currentPage + pages_around_current; i++) {
            if (i > 1 && i < totalPages) {
                range.push(i)
            }
        }
        range.push(totalPages)

        for (const i of range) {
            if (previousPage) {
                if (i - previousPage === 2) {
                    rangeWithDots.push(previousPage + 1)
                } else if (i - previousPage !== 1) {
                    rangeWithDots.push('...')
                }
            }
            rangeWithDots.push(i)
            previousPage = i
        }
        return rangeWithDots
    }

    const visiblePages = getVisiblePages()

    return (
        <PaginationWrapper>
            <PageButton disabled={currentPage === 1} onClick={handlePrevious}>
                <PaginationIcon as={ChevronLeft as StyledSvgComponent} />
            </PageButton>

            {visiblePages.map((page, index) => {
                if (page === '...') {
                    return <PaginationDots key={`dots-${index}`}>...</PaginationDots>
                }

                return (
                    <PageButton key={page} data-page={page} active={page === currentPage} onClick={handlePageClick}>
                        {page}
                    </PageButton>
                )
            })}

            <PageButton disabled={currentPage === totalPages} onClick={handleNext}>
                <PaginationIcon as={ChevronRight as StyledSvgComponent} />
            </PageButton>
        </PaginationWrapper>
    )
}

export default Pagination
